/* Local pricing preview. Shopify checkout needs a trusted version of these rules. */
(function (scope) {
  'use strict';

  // Production-space length breakpoints from the 17 October 2026 pricing workbook.
  // The 60 cm roll prices below cover up to 50 cm of usable width. Designs above
  // 50 cm use the same feed tier plus the width premium approved for the preview.
  // The final tier also covers 151 cm of feed so 150 cm visible artwork fits
  // with its 1 cm production allowance. Prices include GST. Minutes are per unit.
  const BREAKPOINTS = {
    30: [
      [3, 0.5, 9.95, 11.95], [5, 0.5, 9.95, 11.95],
      [10, 1, 11, 14], [15, 1, 12, 16], [20, 1.5, 17, 23],
      [28, 2, 23, 30], [40, 2.5, 29, 40], [58, 3, 36, 50],
      [70, 3.5, 43, 60], [85, 4, 51, 70], [100, 4.5, 58, 81],
      [125, 5, 67, 94], [150, 5.5, 76, 108],
    ],
    60: [
      [10, 1.5, 17, 23], [15, 1.5, 19, 26], [20, 1.5, 21, 29],
      [30, 2, 29, 41], [40, 2.5, 37, 53], [50, 3, 46, 65],
      [58, 4, 57, 81], [85, 5, 76, 110], [100, 6, 91, 130],
      [125, 7, 109, 157], [150, 8, 128, 185],
    ],
  };
  // Additional approved production-feed steps. Their IDs are deliberately
  // appended so a historical B01-B35 order keeps its original meaning.
  const NEW_BREAKPOINTS = {
    30: [
      [25, 1.813, 20, 28, 'B36'], [60, 3.083, 38, 52, 'B37'],
      [115, 4.8, 63, 89, 'B38'], [135, 5.2, 71, 100, 'B39'],
    ],
    60: [
      [25, 1.75, 25, 35, 'B40', 'B44'],
      [70, 4.444, 66, 94, 'B41', 'B45'],
      [115, 6.6, 102, 146, 'B42', 'B46'],
      [135, 7.4, 116, 168, 'B43', 'B47'],
    ],
  };
  const DISCOUNTS = [
    { quantity: 40, rate: 0.55 }, { quantity: 20, rate: 0.50 },
    { quantity: 10, rate: 0.40 }, { quantity: 4, rate: 0.30 },
    { quantity: 2, rate: 0.20 },
  ];
  const WIDE_TIER_PREMIUM = { Gloss: 3, Specialty: 4 };
  // Ex-GST raw vinyl plus transfer tape per running metre. The owner confirmed
  // 60 cm Gloss, Holographic, Gold and Silver materials are available.
  const MEDIA = {
    White: { group: 'Gloss', lane30: 8.4355, lane60: 16.871, sixtyAvailable: true },
    Black: { group: 'Gloss', lane30: 8.4355, lane60: 16.871, sixtyAvailable: true },
    Blue: { group: 'Gloss', lane30: 8.4355, lane60: 16.871, sixtyAvailable: true },
    Pink: { group: 'Gloss', lane30: 8.4355, lane60: 16.871, sixtyAvailable: true },
    Magenta: { group: 'Gloss', lane30: 8.4355, lane60: 16.871, sixtyAvailable: true },
    'Dark Blue': { group: 'Gloss', lane30: 8.4355, lane60: 16.871, sixtyAvailable: true },
    Red: { group: 'Gloss', lane30: 8.4355, lane60: 16.871, sixtyAvailable: true },
    Holographic: { group: 'Specialty', lane30: 12.9305, lane60: 25.861, sixtyAvailable: true },
    'Metallic Gold': { group: 'Specialty', lane30: 13.9855, lane60: 27.971, sixtyAvailable: true },
    'Metallic Silver': { group: 'Specialty', lane30: 13.8605, lane60: 27.721, sixtyAvailable: true },
  };
  const TARGET = { Gloss: 200, Specialty: 250 };
  const PRICING_VERSION = 'breakpoints-v2';
  const PREVIOUS_PRICING_VERSION = 'larger-minimum-v1';
  const LEGACY_PRICING_VERSION = 'legacy-v1';

  function dimensions(width, height, standard = false, largerDecal = false) {
    const productionWidth = width + (standard || largerDecal ? 0.5 : 1);
    const productionHeight = height + 1;
    return {
      long: Math.max(width, height), short: Math.min(width, height), standard, largerDecal,
      productionWidth, productionHeight,
      productionLong: Math.max(productionWidth, productionHeight),
      productionShort: Math.min(productionWidth, productionHeight),
    };
  }

  function routeFor(shape, colour, pricingVersion = PRICING_VERSION) {
    const media = MEDIA[colour];
    if (!media) return null;
    const possible = [];
    for (const lane of [30, 60]) {
      const usableWidth = lane === 30 ? 28 : 58;
      for (const [across, feed] of [
        [shape.productionWidth, shape.productionHeight],
        [shape.productionHeight, shape.productionWidth],
      ]) {
        if (across > usableWidth + 1e-9 || feed > 151 + 1e-9) continue;
        const tierWidthCm = lane === 30 ? 28 : across <= 50 + 1e-9 ? 50 : 58;
        const bandOffset = lane === 30 ? 0 : tierWidthCm === 50 ? 13 : 24;
        const tiers = BREAKPOINTS[lane].map((row, index) => ({
          row, bandId: `B${String(bandOffset + index + 1).padStart(2, '0')}`,
        }));
        if (pricingVersion === PRICING_VERSION) {
          tiers.push(...NEW_BREAKPOINTS[lane].map(row => ({
            row, bandId: lane === 30 || tierWidthCm === 50 ? row[4] : row[5],
          })));
          tiers.sort((a, b) => a.row[0] - b.row[0]);
        }
        const selected = tiers.find(tier => feed <= tier.row[0] + 1e-9) || tiers.at(-1);
        if (!selected) continue;
        const { row: tier, bandId } = selected;
        const widthPremium = lane === 60 && tierWidthCm === 58 ? WIDE_TIER_PREMIUM[media.group] : 0;
        const unitListPrice = tier[media.group === 'Gloss' ? 2 : 3] + widthPremium;
        possible.push({
          lane, acrossCm: across, feedCm: feed,
          tierWidthCm, tierLengthCm: tier[0], tierLabel: `${tierWidthCm} × ${tier[0]}`, bandId,
          minutesPerUnit: tier[1], unitListPrice,
          rawCost: media[`lane${lane}`] * feed / 100,
          provisional: lane === 60 && !media.sixtyAvailable,
        });
      }
    }
    possible.sort((a, b) => a.unitListPrice - b.unitListPrice ||
      a.rawCost - b.rawCost || a.lane - b.lane);
    return possible[0] || null;
  }

  function quote({ widthCm, heightCm, colour = 'White', quantity = 2, iconCount = 1, standard = false, largerDecal = false, heading = false, pricingVersion = PRICING_VERSION }) {
    if (![PRICING_VERSION, PREVIOUS_PRICING_VERSION, LEGACY_PRICING_VERSION].includes(pricingVersion)) {
      return { ok: false, error: 'This pricing version is not supported.' };
    }
    const width = Number(widthCm);
    const height = Number(heightCm);
    const qty = Number(quantity);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return { ok: false, error: 'Enter both visible artwork dimensions in cm.' };
    }
    if (!MEDIA[colour]) return { ok: false, error: 'Choose an available colour.' };
    if (!Number.isInteger(qty) || qty < 1 || qty > 10000) return { ok: false, error: 'Enter a quantity from 1 to 10,000.' };
    if (![0, 1, 2, 3].includes(iconCount)) return { ok: false, error: 'Choose up to three icons.' };
    if (typeof standard !== 'boolean' || typeof largerDecal !== 'boolean' || (standard && largerDecal)) {
      return { ok: false, error: 'Choose a decal size.' };
    }
    if (typeof heading !== 'boolean') return { ok: false, error: 'Choose an available heading.' };
    if (heading && pricingVersion !== PRICING_VERSION) return { ok: false, error: 'This heading price is not available for an earlier pricing version.' };
    if (heading && width < 30 - 1e-9) return { ok: false, error: 'Heading is available from 30 cm length.' };
    const shape = dimensions(width, height, standard, largerDecal);
    if (standard && (width > 23 + 1e-9 || (!heading && height > 3 + 1e-9))) {
      return { ok: false, error: heading ? 'Standard Decal artwork must be 23 cm or less in length.' : 'Standard Decal artwork must fit within 23 × 3 cm.' };
    }
    if (largerDecal && (width > 26 + 1e-9 || (!heading && height > 5 + 1e-9))) {
      return { ok: false, error: heading ? 'Larger Decal artwork must be 26 cm or less in length.' : 'Larger Decal artwork must fit within 26 × 5 cm.' };
    }
    if (width < 4 - 1e-9) return { ok: false, error: 'Length is too small. Min 4 cm.' };
    if (height < 2 - 1e-9) return { ok: false, error: 'Height is too small. Min 2 cm.' };
    if (shape.long > 150 + 1e-9) return { ok: false, error: 'Selected side is too big. Max 150 cm.' };
    if (shape.short > 57 + 1e-9) return { ok: false, error: 'One side must be 57 cm or less.' };
    let route = routeFor(shape, colour, pricingVersion);
    if (!route) return { ok: false, error: 'This size does not fit a 58 × 151 cm production space.' };
    // Keep routing and raw-material use based on the actual geometry. Larger
    // artwork uses at least the existing B03 retail price and time estimate.
    // Unversioned historical orders explicitly request legacy-v1 at intake.
    const requiresLargerMinimum = largerDecal || (!standard &&
      (shape.long > 23 + 1e-9 || shape.short > 3 + 1e-9));
    if (pricingVersion !== LEGACY_PRICING_VERSION && requiresLargerMinimum &&
        ['B01', 'B02'].includes(route.bandId)) {
      const tier = BREAKPOINTS[30][2];
      route = { ...route, baseBandId: route.bandId, minimumPriceApplied: true,
        tierWidthCm: 28, tierLengthCm: tier[0], tierLabel: `28 × ${tier[0]}`, bandId: 'B03',
        minutesPerUnit: tier[1], unitListPrice: tier[MEDIA[colour].group === 'Gloss' ? 2 : 3] };
    }
    if (heading) {
      const allowance = MEDIA[colour].group === 'Gloss' ? 6 : 8;
      route = { ...route, heading: true, baseUnitListPrice: route.unitListPrice,
        baseMinutesPerUnit: route.minutesPerUnit,
        unitListPrice: route.unitListPrice + allowance,
        minutesPerUnit: route.minutesPerUnit + 0.5 };
    }
    const discount = DISCOUNTS.find(tier => qty >= tier.quantity)?.rate || 0;
    const discountPercent = Math.round(discount * 100);
    const beforeCents = Math.round(route.unitListPrice * 100) * qty;
    const afterCents = Math.round(beforeCents * (100 - discountPercent) / 100);
    const beforeDiscount = beforeCents / 100;
    const afterDiscount = afterCents / 100;
    const totalMinutes = route.minutesPerUnit * qty;
    const hourlyContribution = (afterDiscount / 1.1 - qty * route.rawCost) / totalMinutes * 60;
    return {
      ok: true, pricingVersion, artwork: { widthCm: width, heightCm: height }, shape,
      colour, group: MEDIA[colour].group, quantity: qty, iconCount, heading,
      unitListPrice: route.unitListPrice, beforeDiscount,
      discountPercent, totalAfterDiscount: afterDiscount,
      unitAfterDiscount: Math.round(afterCents / qty) / 100,
      route, minutesPerUnit: route.minutesPerUnit,
      estimatedTotalMinutes: totalMinutes, hourlyContribution,
      hourlyTarget: TARGET[MEDIA[colour].group],
      notes: route.provisional ? ['A 60 cm lane for this colour needs a stock check before sale.'] : [],
    };
  }

  const API = { quote, dimensions, routeFor, BREAKPOINTS, NEW_BREAKPOINTS,
    WIDE_TIER_PREMIUM, MEDIA, PRICING_VERSION, PREVIOUS_PRICING_VERSION };
  scope.SSPricingTest = API;
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof window !== 'undefined' ? window : globalThis);
